/**
 * Snapshot interpolation for remote players and replicated mobs: render at
 * `now - INTERP_DELAY`, lerp between the two bracketing snapshots, and when the
 * buffer runs dry extrapolate along the last velocity for at most
 * MAX_EXTRAP_MS before holding still.
 */

export const INTERP_DELAY_MS = 100;
export const MAX_EXTRAP_MS = 200;

export interface Sample { t: number; v: Float64Array }

const wrapAngle = (a: number): number => {
  const TAU = Math.PI * 2;
  a = ((a + Math.PI) % TAU + TAU) % TAU - Math.PI;
  return a;
};

export class SnapshotBuffer {
  private readonly buf: Sample[] = [];
  /** Indices of `v` that hold angles (shortest-arc lerp). */
  constructor(readonly width: number, private readonly angles: readonly number[] = [], private readonly cap = 32) {}

  push(t: number, values: ArrayLike<number>): void {
    const last = this.buf[this.buf.length - 1];
    if (last && t <= last.t) {
      if (t === last.t) last.v.set(values as ArrayLike<number>);
      return; // out-of-order (unreliable channel): drop
    }
    const v = new Float64Array(this.width);
    v.set(values as ArrayLike<number>);
    this.buf.push({ t, v });
    if (this.buf.length > this.cap) this.buf.shift();
  }

  get latest(): Sample | undefined { return this.buf[this.buf.length - 1]; }
  get length(): number { return this.buf.length; }

  /** Interpolated state at `renderT` into `out`; false if empty. */
  sample(renderT: number, out: Float64Array): boolean {
    const b = this.buf;
    if (b.length === 0) return false;
    if (renderT <= b[0].t) { out.set(b[0].v); return true; }
    for (let i = b.length - 1; i > 0; i--) {
      const a = b[i - 1], c = b[i];
      if (a.t <= renderT && renderT <= c.t) {
        const f = (renderT - a.t) / Math.max(1e-6, c.t - a.t);
        this.mix(a.v, c.v, f, out);
        return true;
      }
    }
    // past the newest sample: extrapolate with the last velocity, capped
    const n = b.length;
    const c = b[n - 1];
    if (n < 2) { out.set(c.v); return true; }
    const a = b[n - 2];
    const ahead = Math.min(renderT - c.t, MAX_EXTRAP_MS);
    const f = 1 + ahead / Math.max(1e-6, c.t - a.t);
    this.mix(a.v, c.v, f, out);
    return true;
  }

  private mix(a: Float64Array, c: Float64Array, f: number, out: Float64Array): void {
    for (let i = 0; i < this.width; i++) {
      if (this.angles.includes(i)) out[i] = a[i] + wrapAngle(c[i] - a[i]) * f;
      else out[i] = a[i] + (c[i] - a[i]) * f;
    }
  }
}
